import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { TransactionsView } from "@/modules/eventRegistration/features/EventWaitlist/components/TransactionsView";
import useEventTransactions from "@/modules/eventRegistration/features/EventWaitlist/hooks/useEventTransactions/useEventTransactions";
import RefundModal from "@/modules/eventRegistration/features/EventWaitlist/components/RefundModal/RefundModal";
import VoucherReturnModal from "@/modules/eventRegistration/features/EventWaitlist/components/VoucherReturnModal/VoucherReturnModal";
import InvoiceDetailsModal from "@/modules/eventRegistration/features/EventWaitlist/components/InvoiceDetailsModal/InvoiceDetailsModal";

export default function EventTransactions() {
  const { uuid, eventUuid } = useParams<{ uuid: string; eventUuid: string }>();
  const navigate = useNavigate();

  const {
    registrationGroups,
    isLoading: isTransactionsLoading,
    isProcessing: isTransactionProcessing,
    refreshData: refreshTransactions,
    // Modal states
    refundModalState,
    isRefundModalOpen,
    isGroupRefund,
    refundReason,
    voucherRefundModalState,
    isVoucherRefundModalOpen,
    // Invoice modal states
    selectedInvoices,
    selectedPayments,
    selectedCompanyName,
    selectedConfirmationNumber,
    isInvoiceModalOpen,
    // Modal handlers
    setRefundReason,
    closeRefundModal,
    closeVoucherModal,
    closeInvoiceModal,
    processRefund,
    processVoucherReturn,
  } = useEventTransactions({ eventUuid: uuid });

  const handleBackToWaitlist = () => {
    navigate(`/admin/events/${eventUuid}/sessions/${uuid}/waitlist`);
  };

  return (
    <MainPageWrapper>
      <div className="mb-6">
        <Button className="mb-4" onClick={handleBackToWaitlist} variant="ghost">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Waitlist
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registration Transactions</CardTitle>
          <CardDescription>
            View and manage registration transactions for this session.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TransactionsView
            isLoading={isTransactionsLoading}
            onRefundSuccess={refreshTransactions}
            registrationGroups={registrationGroups}
          />
        </CardContent>
      </Card>

      {/* TRANSACTION MODALS */}
      {/* REFUND MODAL */}
      <RefundModal
        isGroupRefund={isGroupRefund}
        isOpen={isRefundModalOpen}
        isProcessing={isTransactionProcessing}
        onClose={closeRefundModal}
        onProcessRefund={processRefund}
        onReasonChange={setRefundReason}
        refundReason={refundReason}
        selectedRefund={refundModalState}
      />

      {/* VOUCHER RETURN MODAL */}
      <VoucherReturnModal
        isOpen={isVoucherRefundModalOpen}
        isProcessing={isTransactionProcessing}
        onClose={closeVoucherModal}
        onProcessReturn={processVoucherReturn}
        voucherRefund={voucherRefundModalState}
      />

      {/* INVOICE DETAILS MODAL */}
      <InvoiceDetailsModal
        companyName={selectedCompanyName}
        confirmationNumber={selectedConfirmationNumber}
        invoices={selectedInvoices}
        isOpen={isInvoiceModalOpen}
        onClose={closeInvoiceModal}
        payments={selectedPayments}
      />
    </MainPageWrapper>
  );
}
